// src/contexts/AptosWalletContext.tsx
import React, {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { AptosClient } from 'aptos';
import { useDispatch } from 'react-redux';
import { appActions } from 'store/app';
import { toast } from 'react-toastify';

interface PetraWallet {
	account: string | null;
	balance: number;
	connect: () => Promise<void>;
	disconnect: () => Promise<void>;
	getAccountResources: () => Promise<any | null>;
	sendTransaction: (
		recipient: string,
		amount: number,
	) => Promise<string | null>;
	isConnected: boolean;
}

const NODE_URL = 'https://fullnode.mainnet.aptoslabs.com/v1';
const client = new AptosClient(NODE_URL);

// Create a context with a default value of null.
// Consumers should guard against null or ensure they're wrapped by the provider.
const AptosWalletContext = createContext<PetraWallet | null>(null);

type ProviderProps = {
	children: ReactNode;
};

export const AptosWalletProvider: React.FC<ProviderProps> = ({ children }) => {
	const dispatch = useDispatch();

	// Merge account, balance, and isConnected into a single state object
	const [walletState, setWalletState] = useState<{
		account: string | null;
		balance: number;
		isConnected: boolean;
	}>({
		account: null,
		balance: 0,
		isConnected: false,
	});

	const { account, balance, isConnected } = walletState;

	// connect()
	const connect = useCallback(async () => {
		if (!window.aptos) {
			toast('Petra wallet not found', {
				type: 'error',
			});
			return;
		}

		try {
			const response = await window.aptos.connect();
			setWalletState((prev) => ({
				...prev,
				account: response.address,
				isConnected: true,
			}));
			dispatch(appActions.openWallet(false));
		} catch {
			// Connection failed; swallow error
		}
	}, [dispatch]);

	// disconnect()
	const disconnect = useCallback(async () => {
		if (!window.aptos) return;

		try {
			await window.aptos.disconnect();
			setWalletState((prev) => ({
				...prev,
				account: null,
				isConnected: false,
			}));
		} catch {
			// Disconnection failed; swallow error
		}
	}, []);

	// getAccountResources()
	const getAccountResources = useCallback(async () => {
		if (!account) return null;
		try {
			return await client.getAccountResources(account);
		} catch {
			return null;
		}
	}, [account]);

	// sendTransaction()
	const sendTransaction = useCallback(
		async (recipient: string, amount: number) => {
			if (!window.aptos) return null;

			const payload = {
				type: 'entry_function_payload',
				function: '0x1::coin::transfer',
				type_arguments: ['0x1::aptos_coin::AptosCoin'],
				arguments: [recipient, amount],
			};

			try {
				const tx = await window.aptos.signAndSubmitTransaction(payload);
				return tx.hash;
			} catch {
				return null;
			}
		},
		[],
	);

	// On mount, check if wallet is already connected
	useEffect(() => {
		(async () => {
			if (!window.aptos) return;

			try {
				const connected = await window.aptos.isConnected();
				if (connected) {
					const acc = await window.aptos.account();
					setWalletState((prev) => ({
						...prev,
						account: acc.address,
						isConnected: true,
					}));
				}
			} catch {
				// ignore
			}
		})();
	}, []);

	// Memoize the context value to avoid unnecessary re-renders
	const contextValue = useMemo<PetraWallet>(
		() => ({
			account,
			balance,
			connect,
			disconnect,
			getAccountResources,
			sendTransaction,
			isConnected,
		}),
		[
			account,
			balance,
			connect,
			disconnect,
			getAccountResources,
			sendTransaction,
			isConnected,
		],
	);

	console.log(contextValue, 'lop2323');

	return (
		<AptosWalletContext.Provider value={contextValue}>
			{children}
		</AptosWalletContext.Provider>
	);
};

// Custom hook to consume the context
export function useAptosWalletContext(): PetraWallet {
	const context = useContext(AptosWalletContext);
	if (!context) {
		throw new Error(
			'useAptosWalletContext must be used within an AptosWalletProvider',
		);
	}
	return context;
}
